import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { Alert, Divider, Snackbar, Typography } from "@mui/material";
import Highcharts from "highcharts";
import highchartsAccessibility from "highcharts/modules/accessibility";
import highchartExport from "highcharts/modules/exporting";
import highchartNoData from "highcharts/modules/no-data-to-display";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ReportHeaderDetail } from "../../../components/JobPricing/ReportHeaderDetail/ReportHeaderDetail";
import { ReportInfoData } from "../../../components/JobPricing/ReportInfoData/ReportInfoData";
import { ReportInsightsData } from "../../../components/JobPricing/ReportInsightsData/ReportInsightsData";
import { ReportLineChartData } from "../../../components/JobPricing/ReportLineChartData/ReportLineChartData";
import { ReportProgressChartData } from "../../../components/JobPricing/ReportProgressChartData/ReportProgressChartData";
import { ReportTableToggleButton } from "../../../components/JobPricing/ReportTableToggleButton/ReportTableToggleButton";
import { ReportTableView } from "../../../components/JobPricing/ReportTableView/ReportTableView";
import { ReportTargetData } from "../../../components/JobPricing/ReportTargetData/ReportTargetData";
import { PAGE_URL } from "../../../constants/urls.constants";
import {
  cancelReportDetailsAPI,
  cancelSuccessProfileDetailsAPI
} from "../../../services/JobPricingReportService";
import { JobPricingProvider } from "../../../store/JobPricingContext";
import { RootState } from "../../../store/reducers";
import {
  getPercentileDataThunk,
  getReportDetailsThunk,
  resetPricingDetailsState,
  updateReportDetailsApiFailed
} from "../../../store/slices/jobPricingDetailsSlice";
import { resetReportSearchState } from "../../../store/slices/jobPricingSearchSlice";
import {
  disableSearchField,
  enableSearchField,
  setSearchItemClicked
} from "../../../store/slices/jobPricingSlice";
import {
  resetFilters,
  resetFiltersCount,
  resetPeerGroupId,
  updateApiFailed,
  resetSelectedPeerGroup
} from "../../../store/slices/SJPR/listSlice";
import Header from "../Header/index";
import "./ReportDetails.scss";
// Initialize Highcharts modules
highchartsAccessibility(Highcharts);
highchartNoData(Highcharts);
highchartExport(Highcharts);

export const ReportDetails = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useNavigate();
  const { percentileDataList, isReportDetailsApiFailed } = useSelector(
    (state: RootState) => state.jobPricingDetails
  );
  const { jobPricingPayloads } = useSelector(
    (state: RootState) => state.jobPricingSearch
  );
  const { reportViewBy } = useSelector(
    (state: RootState) => state.jobPricingDetails
  );

  const { isApiFailed } = useSelector((state: RootState) => {
    return state.jobPricingList;
  });

  const handleCloseApiFailedMsg = () => {
    dispatch(updateReportDetailsApiFailed(false));
    dispatch(updateApiFailed(false));
  };

  useEffect(() => {
    const reqObj = {
      ...jobPricingPayloads,
      searchType: reportViewBy?.toLowerCase()
    };
    dispatch(getReportDetailsThunk(reqObj));
  }, [jobPricingPayloads]);

  useEffect(() => {
    dispatch(setSearchItemClicked(true));
    if (!percentileDataList || percentileDataList.length === 0) {
      dispatch(getPercentileDataThunk({}));
    }
    dispatch(disableSearchField());
    return () => {
      if (cancelReportDetailsAPI && !cancelReportDetailsAPI.signal.aborted) {
        // cancel the report details API request if it is running
        cancelReportDetailsAPI.abort();
      }
      if (
        cancelSuccessProfileDetailsAPI &&
        !cancelSuccessProfileDetailsAPI.signal.aborted
      ) {
        // cancel the success profile details API request if it is running
        cancelSuccessProfileDetailsAPI.abort();
      }
      dispatch(resetFilters());
      dispatch(resetPeerGroupId());
      dispatch(resetSelectedPeerGroup());
      // dispatch(resetReportSearchState());
      // dispatch(resetPricingDetailsState());
      dispatch(enableSearchField());
      dispatch(resetFiltersCount());
    };
  }, []);

  return (
    <div className="sjpr-report-details">
      <JobPricingProvider>
        <div className="job-pricing-search-filters-container">
          <Header />
        </div>
        <div
          className="back-to-previous-content"
          onClick={() => {
            dispatch(resetFilters());
            dispatch(resetPeerGroupId());
            dispatch(resetPricingDetailsState());
            history(PAGE_URL.JOB_PRICING_LIST);
            dispatch(resetSelectedPeerGroup());
          }}
        >
          <KeyboardBackspaceIcon />
          <Typography variant="body1" sx={{ marginLeft: 1 }}>
            {t("pa.back")}
          </Typography>
        </div>
        <div className="sjpr-report-details-container">
          <ReportHeaderDetail />
          <div className="report-details-section">
            <div className="report-chart-details">
              <div className="target-data-details">
                <ReportTargetData />
              </div>
              <div className="chart-data-details">
                <div style={{ width: "50%" }}>
                  <ReportLineChartData />
                </div>
                <Divider orientation="vertical" variant="middle" flexItem />
                <div style={{ width: "50%" }}>
                  <ReportProgressChartData />
                </div>
              </div>
            </div>
            <div className="report-insight-details">
              <ReportInsightsData />
            </div>
          </div>
          <ReportTableToggleButton />
          <ReportTableView />
          <ReportInfoData />
        </div>
      </JobPricingProvider>
      <Snackbar
        open={isReportDetailsApiFailed || isApiFailed}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        onClose={handleCloseApiFailedMsg}
      >
        <Alert
          onClose={handleCloseApiFailedMsg}
          severity="error"
          sx={{ width: "100%" }}
        >
          {t("pa.sjpr.serviceApiFailedMsg")}
        </Alert>
      </Snackbar>
    </div>
  );
};
